import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { normalizeTier, getQuota, type Tier } from "@/lib/planConfig";

export type PlanTier = Tier;

export interface DailyUsage {
  used: number;
  limit: number;
  remaining: number;        // always clamped ≥ 0
  exhausted: boolean;       // remaining === 0 AND quota window === "day"
  resetsAt: string | null;  // ISO timestamp of next reset
}

const PLAN_CACHE_KEY = (uid: string) => `dz_plan_${uid}`;

function readCachedPlan(userId: string | undefined | null): Tier | null {
  if (!userId) return null;
  try {
    const v = localStorage.getItem(PLAN_CACHE_KEY(userId));
    if (v === "free" || v === "edge" || v === "operator") return v;
  } catch {}
  return null;
}

function writeCachedPlan(userId: string, plan: Tier) {
  try {
    localStorage.setItem(PLAN_CACHE_KEY(userId), plan);
  } catch {}
}

function emptyUsage(tier: Tier): DailyUsage {
  const q = getQuota(tier);
  return {
    used: 0,
    limit: q.max,
    remaining: q.max,
    exhausted: false,
    resetsAt: null,
  };
}

/**
 * DB-backed plan hook.
 *
 * On mount:
 *   1. If signed in, hydrate `plan` from localStorage cache (prevents Free flash
 *      for paid users on first paint).
 *   2. Query DB for actual `current_plan` and reconcile.
 *   3. Query usage_limits via get_daily_usage RPC (clamped, can never go negative).
 *
 * The trial_decisions_used column is no longer read here — it was being
 * incremented forever and produced negative remaining counts.
 */
export function usePlan(): {
  plan: PlanTier;
  loading: boolean;
  usage: DailyUsage;
  refresh: () => Promise<void>;
} {
  const { user } = useAuth();

  // Synchronous initial state — uses cached plan to avoid Free flash.
  const initialPlan: Tier = user ? (readCachedPlan(user.id) ?? "free") : "guest";
  const [plan, setPlan] = useState<Tier>(initialPlan);
  // `loading` is true only on the very first DB reconcile of a fresh session.
  const [loading, setLoading] = useState<boolean>(!!user && !readCachedPlan(user?.id));
  const [usage, setUsage] = useState<DailyUsage>(emptyUsage(initialPlan));

  const load = async () => {
    if (!user) {
      setPlan("guest");
      setLoading(false);
      setUsage(emptyUsage("guest"));
      return;
    }

    // Hydrate from cache first if present (already done synchronously, but
    // refresh() can be called later — make sure we don't drop to Free).
    const cached = readCachedPlan(user.id);
    if (cached && plan !== cached) setPlan(cached);

    const { data: profile } = await supabase
      .from("profiles")
      .select("current_plan")
      .eq("user_id", user.id)
      .maybeSingle();

    const dbPlan = normalizeTier((profile?.current_plan as string | undefined) ?? "free");
    setPlan(dbPlan);
    writeCachedPlan(user.id, dbPlan);

    // Daily usage from clamped RPC. Only meaningful for free tier (paid tiers
    // are monthly-windowed and we don't surface that counter).
    const q = getQuota(dbPlan);
    if (q.window === "day") {
      const { data: usageRow } = await supabase.rpc("get_daily_usage", {
        p_user_id: user.id,
        p_max: q.max,
      });
      const row = Array.isArray(usageRow) ? usageRow[0] : usageRow;
      const used = Math.max(0, Number(row?.used ?? 0));
      const remaining = Math.max(0, Number(row?.remaining ?? q.max - used));
      setUsage({
        used,
        limit: q.max,
        remaining,
        exhausted: remaining === 0,
        resetsAt: row?.resets_at ?? null,
      });
    } else {
      // Monthly tier: we don't surface a hard counter in the UI yet.
      setUsage({
        used: 0,
        limit: q.max,
        remaining: q.max,
        exhausted: false,
        resetsAt: null,
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { plan, loading, usage, refresh: load };
}
